import { NumberBadge } from '@/components/ui/DataTable/NumberBadge'
import { SheetWithFormComponent } from '@/components/ui/SheetWithFormComponent'
import { RestSchemas } from '@/contexts/QueryProvider'
import { IconBoxOutline } from '@icons/IconBoxOutline'
import { IconCopyOutline } from '@icons/IconCopyOutline'
import { IconOpenInNewOutline } from '@icons/IconOpenInNewOutline'
import { ColumnDef } from '@tanstack/react-table'
import clsx from 'clsx'
import { SortingHeaderButton } from '../../../../components/ui/DataTable/SortingHeaderButton'
import { DataAssetsFilesList } from '../../Forms/FilesList/DataAssetsFilesList'
import { TemplatesFilesList } from '../../Forms/FilesList/TemplatesFilesList'
import { RowActionsMenu } from './RowActionsMenu'

type MessagesWorkspacesEntity = RestSchemas['Messages.Workspaces.Entity']

export const workspaceEntitiesColumns: ColumnDef<MessagesWorkspacesEntity>[] = [
  {
    id: 'name',
    accessorKey: 'name',
    header: ({ column }) => <SortingHeaderButton column={column}>Name</SortingHeaderButton>,
    cell: ({ cell }) => (
      <div className="flex flex-row items-center gap-1.5 px-2 py-0">
        <div
          className={clsx(
            'flex size-10 shrink-0 items-center justify-center rounded-full',
            cell.row.original.entityStatus == 0 ? 'bg-primary/5' : 'bg-accent/5'
          )}
        >
          <IconBoxOutline
            className={clsx('size-5', cell.row.original.entityStatus == 0 ? 'text-primary' : 'text-accent')}
          />
        </div>
        <div className="flex flex-col px-2 py-0">
          <span className="text-base">{cell.getValue() as string}</span>
          <span className="text-xs text-muted-foreground">{cell.row.original.guid}</span>
        </div>
      </div>
    ),
    sortingFn: (rowA, rowB) => rowA.original.name.localeCompare(rowB.original.name),
    enableGlobalFilter: true,
    size: 200,
  },
  {
    id: 'entityTypeName',
    accessorFn: (row) => row.entityTypeName + ' ' + row.entityTypeDescription,
    header: ({ column }) => <SortingHeaderButton column={column}>Entity type</SortingHeaderButton>,
    cell: ({ row }) => (
      <div className="flex flex-col px-2 py-0">
        <span className="text-base">{row.original.entityTypeName}</span>
        <span className="text-xs text-muted-foreground">{row.original.entityTypeDescription}</span>
      </div>
    ),
    sortingFn: (rowA, rowB) => rowA.original.entityTypeName.localeCompare(rowB.original.entityTypeName),
    enableGlobalFilter: true,
  },
  {
    id: 'templateFileCount',
    header: 'Template files',
    cell: ({ row }) => {
      return (
        <SheetWithFormComponent
          wide
          title={`${row.original.name} ► Manage template file uploads`}
          trigger={
            <button className="group flex items-center gap-x-2 p-1.5">
              <IconCopyOutline className="size-5 group-hover:text-primary" />
              <NumberBadge value={row.original.templateFileCount ?? 0} />
              <IconOpenInNewOutline className="ml-5 hidden size-4 shrink-0 self-center text-primary group-hover:block" />
            </button>
          }
        >
          <TemplatesFilesList selectedEntity={row.original} />
        </SheetWithFormComponent>
      )
    },
  },
  {
    id: 'dataAssetsFileCount',
    header: 'Data asset files',
    cell: ({ row }) => {
      return (
        <SheetWithFormComponent
          wide
          title={`${row.original.name} ► Manage data asset files`}
          trigger={
            <button className="group flex items-center gap-x-2 p-1.5">
              <IconBoxOutline className="size-5 group-hover:text-primary" />
              <NumberBadge value={row.original.dataAssetFileCount ?? 0} />
              <IconOpenInNewOutline className="ml-5 hidden size-4 shrink-0 self-center text-primary group-hover:block" />
            </button>
          }
        >
          <DataAssetsFilesList selectedEntity={row.original} />
        </SheetWithFormComponent>
      )
    },
  },
  {
    id: 'actions',
    // header: 'Actions',
    cell: ({ row }) => (
      <div className="flex justify-end pr-5">
        <RowActionsMenu entity={row.original} />
      </div>
    ),
  },
]
